import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * GroupSwitcher
 *
 * The third fixed tab in `<AppNav>` (after Home and Collections). The
 * trigger is rendered as a normal `app-nav__tab--fixed` button so the
 * three fixed tabs read as one row — icon-only when inactive, icon +
 * label + keycap when active. Unlike Home/Collections, clicking the
 * trigger doesn't navigate; it opens the group-switcher popover.
 *
 * The tab lights up in its "active" state (expanded with label) when
 * the current page is a group — `activeGroup` carries that signal in
 * from `<AppNav>`. The popover-open state gets its own subtle bg via
 * `aria-expanded="true"` (see `.app-nav__tab--fixed[aria-expanded]` in
 * `app-nav.scss`).
 *
 * Popover content (Radix `<Popover>`):
 *   - The user's groups (collections with `layerIndex = 0`), sorted by
 *     recency. Click → navigate to `/group/:id` and close.
 *   - An inline "New group" form at the bottom: a single text input +
 *     create button. Submit creates a layer-0 collection, navigates to
 *     `/group/:id`, and closes the popover.
 */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faCircleNotch, faPeoplePantsSimple, faPlus, faSpinnerThird, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { Popover, PopoverContent, PopoverTrigger, } from "@shared/client/components/ui/popover/popover";
import { KeyboardShortcut } from "@shared/client/components/ui/keyboard-shortcut/keyboard-shortcut";
import { Tooltip } from "@shared/client/components/ui/tooltip/tooltip";
import { showToast } from "@shared/client/components/toast/show-toast";
import { collectionStore } from "@/data/collection-store";
import { urlForGroup } from "@shared/types/urls";
import "./group-switcher.scss";
const MAX_GROUP_TITLE_LENGTH = 200;
const GROUPS_LABEL = "Groups";
const GROUPS_SHORTCUT = "E";
export const GroupSwitcher = memo(({ icon = faPeoplePantsSimple, activeGroup, openSignal, }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [createPending, setCreatePending] = useState(false);
    const createInputRef = useRef(null);
    const { groups, isLoading } = collectionStore.useListGroups();
    // React to the parent's open-signal so a `E` keypress opens the popover.
    // We only act when the value increments; the very first render keeps
    // `open` false.
    const lastSignal = useRef(openSignal);
    useEffect(() => {
        if (openSignal === undefined)
            return;
        if (lastSignal.current !== openSignal) {
            lastSignal.current = openSignal;
            setOpen(true);
        }
    }, [openSignal]);
    // Reset the inline create form whenever the popover closes so a
    // half-typed title doesn't surprise the user the next time they open
    // it.
    useEffect(() => {
        if (!open) {
            setIsCreating(false);
            setNewTitle("");
            setCreatePending(false);
        }
    }, [open]);
    // Auto-focus the create input when the inline form is revealed.
    useEffect(() => {
        if (isCreating) {
            requestAnimationFrame(() => {
                createInputRef.current?.focus();
            });
        }
    }, [isCreating]);
    const handleSelectGroup = useCallback((group) => {
        setOpen(false);
        navigate(urlForGroup({ id: group.id, slug: group.slug, isImplicit: group.isImplicit }));
    }, [navigate]);
    const handleStartCreate = useCallback(() => {
        setIsCreating(true);
    }, []);
    const handleCreateSubmit = useCallback(async (e) => {
        e.preventDefault();
        const trimmed = newTitle.trim();
        if (trimmed.length === 0 || createPending)
            return;
        setCreatePending(true);
        try {
            const id = await collectionStore.createGroup({ title: trimmed });
            setOpen(false);
            // New group has no slug yet — fall through to the
            // member-only URL. The user can claim a slug later via
            // collection settings.
            navigate(urlForGroup({ id }));
        }
        catch {
            showToast.error("Failed to create group");
        }
        finally {
            setCreatePending(false);
        }
    }, [newTitle, createPending, navigate]);
    const handleCreateKeyDown = useCallback((e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            setIsCreating(false);
            setNewTitle("");
        }
    }, []);
    const isOnGroupPage = activeGroup !== undefined;
    const triggerClasses = [
        "app-nav__tab",
        "app-nav__tab--fixed",
        isOnGroupPage ? "app-nav__tab--active" : "",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsxs(Popover, { open: open, onOpenChange: setOpen, children: [_jsx(Tooltip, { label: GROUPS_LABEL, kbd: GROUPS_SHORTCUT, side: "bottom", children: _jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", className: triggerClasses, "aria-label": isOnGroupPage ? `${GROUPS_LABEL} (current)` : `Open ${GROUPS_LABEL}`, "aria-haspopup": "dialog", children: [_jsx(FaIcon, { icon: icon, className: "app-nav__icon" }), _jsx("span", { className: "app-nav__label", children: GROUPS_LABEL }), _jsx(KeyboardShortcut, { keys: [GROUPS_SHORTCUT], size: "mini", className: "app-nav__kbd", "aria-label": `Shortcut: ${GROUPS_SHORTCUT}` })] }) }) }), _jsxs(PopoverContent, { className: "group-switcher__popover", side: "bottom", align: "start", sideOffset: 8, children: [_jsx("div", { className: "group-switcher__header", children: "Groups" }), _jsx("ul", { className: "group-switcher__list", role: "listbox", children: isLoading && groups.length === 0 ? (_jsxs("li", { className: "group-switcher__status", children: [_jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }), _jsx("span", { children: "Loading groups\u2026" })] })) : groups.length === 0 ? (_jsx("li", { className: "group-switcher__status group-switcher__status--empty", children: "No groups yet. Create one below." })) : (groups.map((group) => {
                            const isActive = activeGroup?.id === group.id;
                            const classes = [
                                "group-switcher__row",
                                isActive ? "group-switcher__row--active" : "",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            return (_jsx("li", { children: _jsxs("button", { type: "button", className: classes, onClick: () => handleSelectGroup(group), role: "option", "aria-selected": isActive, children: [_jsx("span", { className: "group-switcher__row-icon", "aria-hidden": "true", children: group.metadata.emoji ?? _jsx(FaIcon, { icon: faPeoplePantsSimple }) }), _jsx("span", { className: "group-switcher__row-title", children: group.title ?? "Untitled group" }), group.blockCount !== undefined && group.blockCount > 0 ? (_jsx("span", { className: "group-switcher__row-count", children: group.blockCount })) : null] }) }, group.id));
                        })) }), _jsx("div", { className: "group-switcher__footer", children: isCreating ? (_jsxs("form", { className: "group-switcher__create-form", onSubmit: handleCreateSubmit, children: [_jsx("input", { ref: createInputRef, type: "text", className: "group-switcher__create-input", placeholder: "Group name\u2026", value: newTitle, onChange: (e) => setNewTitle(e.target.value), onKeyDown: handleCreateKeyDown, maxLength: MAX_GROUP_TITLE_LENGTH, disabled: createPending, "aria-label": "Group name" }), _jsx("button", { type: "submit", className: "group-switcher__create-submit", disabled: newTitle.trim().length === 0 || createPending, children: createPending ? _jsx(FaIcon, { icon: faCircleNotch, spin: true }) : "Create" })] })) : (_jsxs("button", { type: "button", className: "group-switcher__new-btn", onClick: handleStartCreate, children: [_jsx(FaIcon, { icon: faPlus }), _jsx("span", { children: "New group" })] })) })] })] }));
});
